import { PreviewModal } from "components/ChatPreviewModal/ChatPreviewModal";
import { Button } from "components/DesignSystem/Button/Button";
import { DownloadIcon } from "components/icons/Download";
import { PreviewBody } from "components/PreviewModal";
import { REVENUE_DATA_SOURCE } from "constants/revenueDataSource";
import { invoiceValidation } from "formValidations/invoiceValidation";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReactNode } from "react";
import { useLazyDownloadInvoiceQuery } from "store/apis/invoices";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { Invoice } from "types/Models/invoice";
import { BackendError } from "types/utils/error";
import { stopPropagation } from "utils/stopPropagation";

export const DownloadInvoice = ({
  invoice,
  trigger,
}: {
  invoice: Invoice;
  trigger?: (isLoading: boolean, onClick: () => void) => ReactNode;
}) => {
  const { alertToast } = useToast();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [getInvoiceDownloadUrl, { isLoading, isFetching }] =
    useLazyDownloadInvoiceQuery();
  const [
    getPreviewUrl,
    {
      data: preview,
      isLoading: isPreviewLoading,
      isFetching: isPreviewFetching,
    },
  ] = useLazyGetPreviewUrlQuery();

  const previewModal = useModal();

  const downloadInvoice = async () => {
    const isValid = invoiceValidation.isValidSync(invoice);
    if (!isValid) {
      return alertToast({
        message: "Please fill all the required fields.",
      });
    }

    if (invoice.source === REVENUE_DATA_SOURCE.STRIPE) {
      try {
        await getPreviewUrl({
          groupId: groupId!,
          fileId: invoice?.document?.file_id!,
        }).unwrap();
        previewModal.open();
        return;
      } catch (error) {
        alertToast({ message: (error as BackendError)?.data?.error?.message });
      }
    } else {
      try {
        const { doc_id } = await getInvoiceDownloadUrl({
          groupId: groupId,
          entityId,
          invoiceId: invoice.uuid,
        }).unwrap();
        if (doc_id) {
          await getPreviewUrl({
            groupId: groupId!,
            fileId: doc_id,
          }).unwrap();
          previewModal.open();
        } else {
          alertToast({ message: "No document found to download." });
        }
      } catch (error) {
        alertToast({ message: (error as BackendError)?.data?.error?.message });
      }
    }
  };

  return trigger ? (
    <div>
      {trigger?.(
        isLoading || isFetching || isPreviewLoading || isPreviewFetching,
        downloadInvoice
      )}
      <div onClick={stopPropagation}>
        <PreviewModal
          title={`${invoice.invoice_number}.pdf`}
          open={previewModal.isOpen}
          onClose={previewModal.close}
        >
          {preview && (
            <PreviewBody
              preview_url={preview?.preview_url}
              fullWidth
              file={{
                file_type: "PDF",
                name: `${invoice.invoice_number}.pdf`,
              }}
            />
          )}
        </PreviewModal>
      </div>
    </div>
  ) : (
    <Button
      size="small"
      type="button"
      onClick={downloadInvoice}
      isLoading={
        isLoading || isFetching || isPreviewLoading || isPreviewFetching
      }
      disabled={
        isLoading || isFetching || isPreviewLoading || isPreviewFetching
      }
    >
      <span className="t-flex t-items-center t-gap-1">
        <DownloadIcon strokeWidth="1.5" color="currentColor" />
        <span>Download</span>
      </span>

      <div onClick={stopPropagation}>
        <PreviewModal
          title={`${invoice.invoice_number}.pdf`}
          open={previewModal.isOpen}
          onClose={previewModal.close}
        >
          {preview && (
            <PreviewBody
              preview_url={preview?.preview_url}
              fullWidth
              file={{
                file_type: "PDF",
                name: `${invoice.invoice_number}.pdf`,
              }}
            />
          )}
        </PreviewModal>
      </div>
    </Button>
  );
};
